import axios from 'axios';
import { BASE_URL } from './connection';

const s3Api = axios.create({
    baseURL: `${BASE_URL}/api/v1/files`,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json'
    }
});

export const s3UploadApi = {
    // 生成预签名上传URL
    generateUploadUrl: async (fileName, fileType) => {
        try {
            const response = await s3Api.post('/upload-url', {
                file_name: fileName,
                file_type: fileType
            });
            return response.data;
        } catch (error) {
            if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error')) {
                throw new Error('无法连接到服务器，请检查后端服务是否启动');
            }
            throw error;
        }
    },

    // 直接上传文件到S3
    // extraHeaders: 可选，覆盖或补充分签所需headers（如 Content-Type）
    // 返回值: { success: boolean, status?: number, statusText?: string, message?: string, uploadDurationMs?: number }
    uploadFileToS3: async (uploadUrl, file, onProgress, extraHeaders) => {
        const startTime = Date.now();
        try {
            // 检查是否为开发模式（模拟URL）
            if (uploadUrl.includes('mock-s3-upload.dev')) {
                // 模拟上传进度
                return new Promise((resolve, reject) => {
                    let progress = 0;
                    const interval = setInterval(() => {
                        progress += Math.random() * 25;
                        if (progress > 100) progress = 100;

                        if (onProgress) {
                            onProgress(Math.floor(progress));
                        }

                        if (progress >= 100) {
                            clearInterval(interval);

                            // 模拟5%的失败概率
                            if (Math.random() < 0.05) {
                                reject(new Error('模拟网络错误：上传超时'));
                            } else {
                                resolve({
                                    success: true,
                                    status: 200,
                                    statusText: 'OK',
                                    message: 'Mock upload success',
                                    uploadDurationMs: Date.now() - startTime
                                });
                            }
                        }
                    }, 200);
                });
            } else {
                // 生产模式：真实上传到S3
                // 仅在签名要求时才发送 Content-Type，避免签名不匹配
                let headers = { ...(extraHeaders || {}) };
                const shouldSendContentType = (() => {
                    try {
                        const urlObj = new URL(uploadUrl);
                        const signedHeadersParam =
                            urlObj.searchParams.get('X-Amz-SignedHeaders') ||
                            urlObj.searchParams.get('x-amz-signedheaders');
                        return signedHeadersParam
                            ? signedHeadersParam.split(';').some(h => h.trim().toLowerCase() === 'content-type')
                            : false;
                    } catch (_e) {
                        return false;
                    }
                })();

                // 只设置签名要求的头部
                headers = {};
                if (shouldSendContentType) {
                    headers['Content-Type'] = (extraHeaders && extraHeaders['Content-Type']) || file.type || 'application/octet-stream';
                }

                // 使用fetch，设置正确的Content-Type
                const response = await fetch(uploadUrl, {
                    method: 'PUT',
                    headers,
                    body: file
                });

                if (!response.ok) {
                    return {
                        success: false,
                        status: response.status,
                        statusText: response.statusText,
                        message: `Backend signature error - HTTP ${response.status}: ${response.statusText}`,
                        uploadDurationMs: Date.now() - startTime
                    };
                }

                console.log('[S3Upload] Upload successful:', response.status);
                return {
                    success: true,
                    status: response.status,
                    statusText: response.statusText,
                    uploadDurationMs: Date.now() - startTime
                };
            }
        } catch (error) {
            // fetch 错误处理
            if (error?.message?.includes('HTTP 403')) {
                console.error('上传文件到S3失败: HTTP 403');
                return {
                    success: false,
                    status: 403,
                    statusText: 'Forbidden',
                    message: 'S3上传403:签名与请求不一致或已过期',
                    uploadDurationMs: Date.now() - startTime
                };
            }
            // 尝试从 error message 中提取状态码
            const statusMatch = error?.message?.match(/HTTP (\d+)/);
            const status = statusMatch ? parseInt(statusMatch[1]) : 0;
            console.error('上传文件到S3失败:', error);
            return {
                success: false,
                status,
                statusText: error?.message || 'Unknown error',
                message: error?.message || 'Unknown error',
                uploadDurationMs: Date.now() - startTime
            };
        }
    },

    // 获取文件访问URL
    getAccessUrl: async (fileKey) => {
        try {
            const response = await s3Api.post('/access-url', { file_key: fileKey });
            return response.data;
        } catch (error) {
            if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error')) {
                throw new Error('无法连接到服务器，请检查后端服务是否启动');
            } else if (error.response?.status >= 400) {
                throw new Error(`获取访问URL失败: ${error.response?.data?.detail || error.message}`);
            } else {
                throw new Error(`获取访问URL失败: ${error.message}`);
            }
        }
    }
};
